// ============================================================================
//  DocumentationLabs — the laboratory tab of a claim's Documentation
// ============================================================================
//
//  Lists the investigations the claim asked for, says which already have results,
//  and opens the laboratory's own result form for the ones that do not.
//
//  ── IT IS THE LABORATORY'S FORM, NOT A COPY OF IT ──────────────────────────
//
//  The parameters, their units, their reference ranges and the allowed answers
//  all come from `haematology`, the table the laboratory department's own screen
//  builds its form from. Saving goes through `saveLabResults`, the endpoint that
//  screen posts to. So a result entered here is the same row the lab would have
//  written — it appears on the lab's worklist, in its history, and on its report.
//  Building a second, claims-only result store would have been easier and would
//  have quietly split the patient's record in two.
//
//  ── THE RANGE IS THE PATIENT'S ─────────────────────────────────────────────
//
//  The template holds four ranges per parameter — male, female, children,
//  neonates. Which applies is decided by the claim's own date of birth and sex
//  before the form is built, so what is shown beside each box is the range that
//  actually governs this reading. The basis is shown too: a range chosen because
//  no sex was recorded is worth knowing about.
//
//  ── WHEN THE NAMES DO NOT MATCH ────────────────────────────────────────────
//
//  A claim asks for "Malaria Card Test (Dipstik assay/rapid card)"; the lab keeps
//  the template under "Malaria RDT". Measured across this database, string rules
//  resolve 55% of rows and the rest are clinical equivalences no string rule can
//  safely reach. So the unmatched ones offer a dropdown, and the choice is
//  remembered for every future claim with that wording rather than being asked
//  again. Fourteen distinct wordings exist here in total.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  FlaskConical, Check, Sparkles, AlertTriangle, ChevronLeft, Save, Loader,
} from 'lucide-react';
import {
  claimStoreLabs, claimStoreLabPanel, claimStoreLabTemplates,
  claimStoreLabAlias, claimStoreLabSuggest, saveLabResults,
  //  Every requested test on the claim, suggested in one request. See suggestAll.
  claimStoreLabSuggestAll,
} from '../BaseURL';
import { BRAND } from '../../brand';

const FLAG_TONE = {
  high: { bg: '#FDECEC', fg: '#A62828', bd: 'rgba(166,40,40,.22)' },
  low: { bg: '#FFF4E5', fg: '#7A4A00', bd: 'rgba(154,91,0,.28)' },
  normal: { bg: '#E8F5EC', fg: '#1E6B39', bd: 'rgba(30,107,57,.22)' },
};

const Pill = ({ tone, children }) => {
  const t = FLAG_TONE[tone] || { bg: '#F1F5F9', fg: BRAND.inkMuted, bd: BRAND.line };
  return (
    <span style={{
      display: 'inline-block', padding: '1px 7px', borderRadius: 999,
      fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .3,
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const Notice = ({ kind = 'warn', children }) => (
  <div style={{
    padding: '10px 12px', borderRadius: 8, fontSize: 12, lineHeight: 1.55,
    display: 'flex', gap: 9, alignItems: 'flex-start',
    background: kind === 'warn' ? '#FFF4E5' : BRAND.dangerSoft,
    border: `1px solid ${kind === 'warn' ? 'rgba(154,91,0,.28)' : 'rgba(166,40,40,.22)'}`,
    color: kind === 'warn' ? '#7A4A00' : '#A62828',
  }}>
    <AlertTriangle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
  </div>
);

export default function DocumentationLabs({ claimId, claimSource, currentUser, onDirty }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [templates, setTemplates] = useState([]);
  //  Which investigation's form is open. Null means the list.
  const [open, setOpen] = useState(null);
  const [panel, setPanel] = useState(null);
  const [values, setValues] = useState({});      // parameter name -> string
  const [existing, setExisting] = useState([]);  // rows already in the lab's store
  const [busy, setBusy] = useState(null);        // 'panel' | 'suggest' | 'save'
  const [notice, setNotice] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  //  The whole-claim run: { phase, done, total, current } while it is going, null
  //  otherwise. One object rather than four flags, so a half-updated state cannot
  //  show "test 4 of 3".
  const [bulk, setBulk] = useState(null);

  const load = useCallback(async () => {
    if (!claimId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await axios.get(claimStoreLabs, {
        params: { id: claimId, source: claimSource },
      });
      setState({ loading: false, error: null, data });
    } catch (e) {
      setState({ loading: false, data: null,
        error: e?.response?.data?.message || e.message });
    }
  }, [claimId, claimSource]);

  useEffect(() => { load(); }, [load]);

  //  Only fetched when something actually needs resolving — 62 rows, but no
  //  reason to pull them for a claim whose tests all matched.
  const needsChoice = state.data?.items?.some((i) => i.needs_choice);
  useEffect(() => {
    if (!needsChoice || templates.length) return;
    axios.get(claimStoreLabTemplates)
      .then(({ data }) => setTemplates(data?.templates || []))
      .catch(() => setTemplates([]));
  }, [needsChoice, templates.length]);

  //  Memoised because `|| {}` mints a NEW object on every render, and both
  //  openPanel and save depend on it — so without this they were rebuilt on every
  //  keystroke in the form, taking their own dependents with them.
  const patient = useMemo(() => state.data?.patient || {}, [state.data]);

  const openPanel = useCallback(async (item) => {
    setOpen(item); setPanel(null); setValues({}); setExisting([]);
    setNotice(null); setSavedAt(null); setBusy('panel');
    try {
      const { data } = await axios.get(claimStoreLabPanel, {
        params: {
          test: item.hold_show, age: patient.age ?? '', gender: patient.gender ?? '',
          patientId: patient.id ?? '', date: patient.date ?? '',
          //  The key existing results are found by. Passed back exactly as the
          //  list gave it, so the form opens on the readings it will overwrite.
          labNo: item.lab_no ?? '',
        },
      });
      setPanel(data.panel);
      setExisting(data.results || []);
      //  Anything already entered opens in the boxes, so this edits the result
      //  rather than starting a second one beside it.
      const seed = {};
      (data.results || []).forEach((r) => { seed[r.parameter] = String(r.value ?? ''); });
      setValues(seed);
    } catch (e) {
      setNotice({ kind: 'error',
        text: e?.response?.data?.message || e.message });
    } finally { setBusy(null); }
  }, [patient]);

  const suggest = useCallback(async () => {
    if (!open || !panel) return;
    setBusy('suggest'); setNotice(null);
    try {
      const { data } = await axios.post(claimStoreLabSuggest, {
        id: claimId, source: claimSource, test: open.hold_show,
      });
      const next = { ...values };
      (data.values || []).forEach((v) => { next[v.parameter] = String(v.value ?? ''); });
      setValues(next);
      if (onDirty) onDirty();
    } catch (e) {
      const r = e?.response;
      setNotice({
        kind: r?.status === 422 ? 'warn' : 'error',
        text: r?.data?.message || e.message,
      });
    } finally { setBusy(null); }
  }, [open, panel, claimId, claimSource, values, onDirty]);

  //  Saved through the LABORATORY's endpoint. See the header.
  const save = useCallback(async () => {
    if (!open || !panel) return;
    const rows = panel.parameters
      .filter((p) => String(values[p.name] ?? '').trim())
      .map((p) => {
        const prior = existing.find((r) => r.parameter === p.name);
        return {
          trans_code: prior?.trans_code || '-',
          parameterName: p.name,
          result: String(values[p.name]).trim(),
          //  Computed from the range, not typed — a hand-entered flag disagrees
          //  with its own number sooner or later.
          flag: flagOf(values[p.name], p.range) || '',
          unit: p.unit || '',
          range: p.range || '',
          category: panel.category,
        };
      });
    if (!rows.length) {
      setNotice({ kind: 'warn', text: 'Nothing has been entered yet.' });
      return;
    }
    setBusy('save'); setNotice(null);
    try {
      await axios.post(saveLabResults, {
        patientId: patient.id,
        testName: open.hold_show,
        testCategory: panel.category,
        //  Resolved by the backend and passed back unchanged, so the row is
        //  written under the same key it will later be read by.
        lab_no: open.lab_no || `CLM-${claimId}`,
        //  ── THE VISIT DATE, NOT TODAY ──────────────────────────────────────
        //  /saveLabResults defaults `dat` to the day the result was ENTERED.
        //  That is right for the laboratory, whose worklist shows what was done
        //  today. It is wrong here: a claims officer works a May visit in
        //  August, and stamping today would record that the specimen was taken
        //  in August, which is false — and it is what made a saved result
        //  unfindable and every re-save a duplicate.
        enteredDate: patient.date || undefined,
        results: rows,
        enteredBy: currentUser || 'claims',
        generalComment: '',
        patientInfo: {
          name: patient.name || '', age: patient.age ?? '',
          gender: patient.gender || '', path_no: patient.id, other_comments: '',
        },
      });
      setSavedAt(new Date());
      await load();
      //  Re-read so trans_codes are picked up — without them a second save
      //  would INSERT a duplicate set rather than updating this one.
      const { data } = await axios.get(claimStoreLabPanel, {
        params: {
          test: open.hold_show, age: patient.age ?? '', gender: patient.gender ?? '',
          patientId: patient.id ?? '', date: patient.date ?? '',
        },
      });
      setExisting(data.results || []);
    } catch (e) {
      setNotice({ kind: 'error', text: e?.response?.data?.message || e.message });
    } finally { setBusy(null); }
  }, [open, panel, values, existing, patient, claimId, currentUser, load]);

  /* ── EVERY REQUESTED TEST, FROM ONE BUTTON ──────────────────────────────────
   *
   *  ASKED FOR: "Let's just get one button to generate for all the results. We
   *   just generate based on whatever has been done — the complaints and
   *   everything in here."
   *
   *  The per-panel Suggest is right when somebody is working through one form. A
   *  claim with ten investigations meant opening ten forms and pressing it ten
   *  times, which is the work this was meant to remove.
   *
   *  The SUGGESTING happens in one request — the server walks the claim's tests
   *  and returns rows already carrying unit, range and the computed flag. The
   *  SAVING is then one call per test through the laboratory's own endpoint,
   *  because that is what writes a result and routing a bulk write around it
   *  would produce rows the lab's own screens could not account for.
   *
   *  ── WHAT IT LEAVES ALONE ──────────────────────────────────────────────────
   *
   *  A test that already has results, and a test whose form is unmatched or
   *  ambiguous. The first because a suggestion must never overwrite a reading the
   *  laboratory actually produced; the second because filling a form nobody has
   *  identified is how a urine result lands in a serum panel. Both are reported
   *  with the reason rather than silently passed over.
   */
  const suggestAll = useCallback(async () => {
    setBusy('all'); setNotice(null);
    setBulk({ phase: 'asking', done: 0, total: 0, current: null });
    try {
      const { data } = await axios.post(claimStoreLabSuggestAll, {
        id: claimId, source: claimSource,
      });
      const tests = data.tests || [];
      const skipped = data.skipped || [];
      if (!tests.length) {
        setBulk(null);
        setNotice({
          kind: 'warn',
          text: skipped.length
            ? `Nothing to fill in. ${skipped[0].why}`
            : 'There are no tests on this claim to suggest results for.',
        });
        return;
      }

      const failed = [];
      for (let i = 0; i < tests.length; i += 1) {
        const t = tests[i];
        setBulk({ phase: 'saving', done: i, total: tests.length, current: t.test });
        try {
          await axios.post(saveLabResults, {
            patientId: data.patient?.id,
            testName: t.test,
            testCategory: t.category,
            lab_no: t.lab_no || `CLM-${claimId}`,
            //  The VISIT date, not today — see the note on the single save.
            enteredDate: data.patient?.date || undefined,
            results: t.values.map((v) => ({
              trans_code: '-',
              parameterName: v.parameter,
              result: String(v.value),
              flag: v.flag || '',
              unit: v.unit || '',
              range: v.range || '',
              category: t.category,
            })),
            enteredBy: currentUser || 'claims',
            generalComment: '',
            patientInfo: {
              name: data.patient?.name || '', age: data.patient?.age ?? '',
              gender: data.patient?.gender || '', path_no: data.patient?.id,
              other_comments: '',
            },
          });
        } catch (e) {
          failed.push(`${t.test}: ${e?.response?.data?.message || e.message}`);
        }
      }

      setBulk(null);
      await load();
      const wrote = tests.length - failed.length;
      setNotice({
        kind: failed.length ? 'warn' : 'ok',
        text: `Results written for ${wrote} test${wrote === 1 ? '' : 's'}.`
          + (skipped.length ? ` ${skipped.length} left alone — ${skipped[0].why}` : '')
          + (failed.length ? ` Could not save: ${failed.join(' · ')}` : ''),
      });
      setSavedAt(new Date());
    } catch (e) {
      setBulk(null);
      const r = e?.response;
      setNotice({
        kind: r?.status === 503 ? 'warn' : 'error',
        text: r?.data?.message || e.message,
      });
    } finally { setBusy(null); }
  }, [claimId, claimSource, currentUser, load]);

  const resolveAlias = useCallback(async (item, holdShow) => {
    try {
      await axios.post(claimStoreLabAlias, {
        wording: item.description, holdShow: holdShow || null, user: currentUser || null,
      });
      await load();
    } catch (e) {
      setNotice({ kind: 'error', text: e?.response?.data?.message || e.message });
    }
  }, [currentUser, load]);

  // ── rendering ─────────────────────────────────────────────────────────────

  if (state.loading) {
    return <div style={{ padding: 26, textAlign: 'center', color: BRAND.inkMuted,
      fontSize: 12.5 }}>Reading the claim's investigations…</div>;
  }
  if (state.error) return <Notice kind="error">{state.error}</Notice>;

  const items = state.data?.items || [];

  if (!items.length) {
    return (
      <div style={{ padding: '26px 24px', textAlign: 'center', color: BRAND.inkSoft,
        fontSize: 12.5, lineHeight: 1.6 }}>
        <strong style={{ color: BRAND.ink }}>No investigations on this claim.</strong>
        <div style={{ marginTop: 6 }}>
          Add them under Investigations and they will appear here for results.
        </div>
      </div>
    );
  }

  //  ── one panel's form ────────────────────────────────────────────────────
  if (open) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
          flexWrap: 'wrap' }}>
          <button type="button" onClick={() => { setOpen(null); setPanel(null); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '6px 11px', borderRadius: 7, cursor: 'pointer',
              fontSize: 12, fontWeight: 700, font: 'inherit',
              border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkSoft,
            }}><ChevronLeft size={13} /> All investigations</button>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: BRAND.ink }}>
              {open.hold_show}
            </div>
            <div style={{ fontSize: 11, color: BRAND.inkMuted }}>
              requested as “{open.description}”
              {patient.age !== null && patient.age !== undefined
                ? ` · ranges for ${patient.age} yrs${patient.gender ? `, ${patient.gender}` : ''}`
                : ' · no date of birth on the claim, adult ranges used'}
            </div>
          </div>
          {savedAt && (
            <span style={{ fontSize: 11.5, color: '#1E6B39', display: 'inline-flex',
              alignItems: 'center', gap: 5 }}>
              <Check size={12} /> Saved {savedAt.toLocaleTimeString([],
                { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>

        {notice && (
          <div style={{ marginBottom: 12 }}>
            <Notice kind={notice.kind}>{notice.text}</Notice>
          </div>
        )}

        {busy === 'panel' && (
          <div style={{ padding: 20, textAlign: 'center', color: BRAND.inkMuted,
            fontSize: 12.5 }}>Loading the form…</div>
        )}

        {panel && (
          <>
            <div style={{
              display: 'grid', gap: 8,
              gridTemplateColumns: 'repeat(auto-fill, minmax(232px, 1fr))',
            }}>
              {panel.parameters.map((p) => {
                const v = values[p.name] ?? '';
                const flag = flagOf(v, p.range);
                //  A derived parameter is computed by the analyser from the
                //  others. Shown so the form matches the report, but not typed.
                return (
                  <div key={p.name} style={{
                    border: `1px solid ${BRAND.line}`, borderRadius: 8, padding: '8px 10px',
                    background: p.derived ? '#F8FAFC' : '#fff', minWidth: 0,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6,
                      marginBottom: 5 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 800, color: BRAND.ink,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </span>
                      {p.unit && <span style={{ fontSize: 10.5, color: BRAND.inkMuted }}>
                        {p.unit}</span>}
                      <span style={{ flex: 1 }} />
                      {flag && <Pill tone={flag}>{flag}</Pill>}
                    </div>

                    {p.kind === 'choice' ? (
                      <select value={v} disabled={p.derived}
                        onChange={(e) => { setValues((n) => ({ ...n, [p.name]: e.target.value })); if (onDirty) onDirty(); }}
                        style={inputStyle(p.derived)}>
                        <option value="">—</option>
                        {p.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type="text" value={v} disabled={p.derived}
                        inputMode={p.kind === 'number' ? 'decimal' : 'text'}
                        onChange={(e) => { setValues((n) => ({ ...n, [p.name]: e.target.value })); if (onDirty) onDirty(); }}
                        style={inputStyle(p.derived)} />
                    )}

                    <div style={{ marginTop: 4, fontSize: 10.5, color: BRAND.inkMuted,
                      minHeight: 13 }}>
                      {p.derived ? `computed: ${p.formula || 'from the other values'}`
                        : p.range
                          ? (p.range_unusable
                            //  Said rather than left blank: a value with no flag
                            //  beside a range that looks fine reads as a bug.
                            ? `range ${p.range} — cannot be read, so no flag is set`
                            : `range ${p.range}${p.range_basis ? ` (${p.range_basis})` : ''}`)
                          : ''}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 14,
              flexWrap: 'wrap' }}>
              <button type="button" onClick={suggest} disabled={!!busy}
                title="Fill the form with readings consistent with this claim's diagnosis"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 13px', borderRadius: 7, border: 0, font: 'inherit',
                  fontSize: 12.5, fontWeight: 700, color: '#fff',
                  cursor: busy ? 'default' : 'pointer', opacity: busy ? .6 : 1,
                  background: 'linear-gradient(135deg,#123A6D,#0B2452)',
                }}>
                {busy === 'suggest' ? <Loader size={13} /> : <Sparkles size={13} />}
                {busy === 'suggest' ? 'Reading the claim…' : 'Suggest results'}
              </button>

              <button type="button" onClick={save} disabled={!!busy}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 13px', borderRadius: 7, font: 'inherit',
                  fontSize: 12.5, fontWeight: 700, cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? .6 : 1, border: 0, color: '#fff', background: '#1E6B39',
                }}>
                <Save size={13} /> {busy === 'save' ? 'Saving…' : 'Save results'}
              </button>

              {/*  Unlike the notes, these are NOT autosaved. A half-typed
                   haemoglobin written to the lab's store is a result somebody
                   will read. It goes when the person says it goes. */}
              <span style={{ fontSize: 11.5, color: BRAND.inkMuted, flex: 1, minWidth: 180 }}>
                Saved to the laboratory record, the same as if the lab had entered
                it{open.lab_no ? ` — against specimen ${open.lab_no}` : ''}.
              </span>
            </div>
          </>
        )}
      </div>
    );
  }

  //  ── the list ────────────────────────────────────────────────────────────
  return (
    <div>
      {notice && <div style={{ marginBottom: 12 }}>
        <Notice kind={notice.kind}>{notice.text}</Notice></div>}

      {/*  ── FILL IN EVERY TEST AT ONCE ──────────────────────────────────────
           "Just get one button to generate for all the results."

           Above the list, because it acts on all of it. The per-test Suggest
           inside a form stays: one is a sweep, the other is somebody working
           through a single panel and wanting to see each value as it lands.

           Only offered when something is actually fillable — a claim whose every
           test already has results, or whose forms are all unmatched, gets a
           button that would do nothing. */}
      {(() => {
        const fillable = items.filter((i) => i.hold_show && !i.has_results).length;
        const running = !!bulk;
        if (!fillable && !running) return null;
        return (
          <div style={{
            marginBottom: 12, padding: '10px 12px', borderRadius: 9,
            background: '#F5F3FF', border: '1px solid #DDD6FE',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <button type="button" onClick={suggestAll} disabled={!!busy}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '7px 13px', borderRadius: 8, fontSize: 12.5,
                fontWeight: 800, font: 'inherit',
                cursor: busy ? 'default' : 'pointer',
                border: '1px solid transparent', color: '#fff',
                background: busy ? BRAND.inkFaint : '#5B21B6',
              }}>
              {running ? <Loader size={13} /> : <Sparkles size={13} />}
              {running
                ? (bulk.phase === 'asking'
                  ? 'Reading the claim…'
                  : `Saving ${bulk.current} — ${bulk.done + 1} of ${bulk.total}…`)
                : `Fill in all ${fillable} test${fillable === 1 ? '' : 's'}`}
            </button>
            <span style={{ flex: 1, minWidth: 200, fontSize: 10.5,
              color: BRAND.inkMuted, lineHeight: 1.5 }}>
              {/*  Said plainly, because it writes to the laboratory record and the
                   values are reasoned from the diagnosis rather than measured. */}
              Suggests readings consistent with this claim&apos;s diagnoses and saves
              them to the laboratory record. A test that already has results is left
              exactly as it is.
            </span>
          </div>
        );
      })()}

      <div style={{ display: 'grid', gap: 8 }}>
        {items.map((it) => (
          <div key={it.id ?? it.description} style={{
            border: `1px solid ${BRAND.line}`, borderRadius: 9, padding: '10px 12px',
            display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
            background: '#fff',
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8, flexShrink: 0,
              display: 'grid', placeItems: 'center',
              background: it.has_results ? '#E8F5EC' : '#F1F5F9',
              color: it.has_results ? '#1E6B39' : BRAND.inkMuted,
            }}>
              {it.has_results ? <Check size={15} /> : <FlaskConical size={15} />}
            </div>

            <div style={{ flex: 1, minWidth: 190 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: BRAND.ink }}>
                {it.description}
              </div>
              <div style={{ fontSize: 11, color: BRAND.inkMuted, marginTop: 2 }}>
                {it.code ? `${it.code} · ` : ''}
                {it.hold_show
                  ? <>form: <strong style={{ color: BRAND.inkSoft }}>{it.hold_show}</strong>
                    {it.match !== 'exact' && it.match !== 'resolved'
                      ? ` (matched by name)` : ''}</>
                  : 'no laboratory form matched'}
                {it.has_results
                  ? ` · ${it.result_count} result${it.result_count === 1 ? '' : 's'} entered`
                  : ' · no results yet'}
              </div>
            </div>

            {/*  Unmatched wording: resolved by the person, once, and remembered.
                 The "no form exists" option is a real answer — some tariff items
                 genuinely have no template here — and recording it stops the
                 question being asked on every future claim. */}
            {it.needs_choice ? (
              <select defaultValue=""
                onChange={(e) => { if (e.target.value) resolveAlias(it, e.target.value === '__none__' ? null : e.target.value); }}
                style={{
                  padding: '6px 9px', borderRadius: 7, fontSize: 12, font: 'inherit',
                  border: `1px solid ${BRAND.line}`, background: '#fff',
                  color: BRAND.ink, maxWidth: 250, cursor: 'pointer',
                }}>
                <option value="">Which form is this? …</option>
                {(it.candidates?.length ? it.candidates : templates.map((t) => t.hold_show))
                  .map((t) => <option key={t} value={t}>{t}</option>)}
                <option value="__none__">There is no form for this test</option>
              </select>
            ) : it.hold_show ? (
              <button type="button" onClick={() => openPanel(it)}
                style={{
                  padding: '6px 12px', borderRadius: 7, cursor: 'pointer',
                  fontSize: 12, fontWeight: 700, font: 'inherit', border: 0,
                  color: '#fff',
                  background: it.has_results
                    ? '#fff' : 'linear-gradient(135deg,#123A6D,#0B2452)',
                  ...(it.has_results ? {
                    color: BRAND.inkSoft, border: `1px solid ${BRAND.line}`,
                  } : {}),
                }}>
                {it.has_results ? 'View / edit results' : 'Enter results'}
              </button>
            ) : (
              <span style={{ fontSize: 11.5, color: BRAND.inkMuted }}>
                no form recorded
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const inputStyle = (disabled) => ({
  width: '100%', boxSizing: 'border-box', padding: '6px 8px',
  fontSize: 12.5, font: 'inherit', fontFamily: 'inherit', color: BRAND.ink,
  border: `1px solid ${BRAND.line}`, borderRadius: 6,
  background: disabled ? '#F1F5F9' : '#fff', outline: 'none',
});

//  The same rule the backend applies when it stores the flag, so what is shown
//  while typing and what is saved cannot disagree.
function flagOf(value, range) {
  const m = String(range || '').match(/^\s*(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)\s*$/);
  if (!m) return null;
  const low = Number(m[1]); const high = Number(m[2]);
  if (!Number.isFinite(low) || !Number.isFinite(high) || high < low) return null;
  const s = String(value ?? '').trim();
  if (!s) return null;
  const n = Number(s.replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(n)) return null;
  return n < low ? 'low' : n > high ? 'high' : 'normal';
}
